// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import React, { useState } from 'react';
import qs from 'query-string';
import {
  Check,
  Gavel,
  Loader2,
  LockIcon,
  MoreVertical,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShieldQuestion,
} from 'lucide-react';
import { MemberRole, Server } from '@/models';
import { Modal, ModalBody, ModalContent, ModalHeader } from '@heroui/react';
import { type ModalProps, useModal } from '@/stores/use-modal-store.ts';
import { UserAvatar } from '@/components/user-avatar';
import { toast } from 'sonner';
import { API_BASE } from '@/config';
import { useTranslation } from 'react-i18next';
import { useSocket } from '@/context/socket-context/use-external-socket.ts';
import { useNavigate } from 'react-router';

const roleIconMap = {
  GUEST: null,
  PRIVATE: <LockIcon className="ml-2 size-4 text-gray-500" />,
  MODERATOR: <ShieldCheck className="ml-2 size-4 text-indigo-500" />,
  ADMIN: <ShieldAlert className="size-4 text-rose-500" />,
};

export const MembersModal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  data,
}) => {
  const { t } = useTranslation();
  const [loadingId, setLoadingId] = useState('');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const toggleMenu = (id: string) => {
    setOpenMenuId((prev) => (prev === id ? null : id));
  };
  const { server } = data as { server: Server };
  const socket = useSocket();
  const { onOpen } = useModal();
  const navigate = useNavigate();

  const onKick = async (memberId: string) => {
    try {
      setLoadingId(memberId);
      const url = qs.stringifyUrl({
        url: `${API_BASE}/api/members/${memberId}`,
        query: {
          serverId: server?.id,
        },
      });

      const response = await fetch(url, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();

      socket?.send(
        JSON.stringify({
          type: 'member-leave',
          payload: data,
        })
      );
      navigate(`/servers/${server.id}`, { replace: true });
      // onOpen('members', { server: data });
    } catch (error) {
      toast.error(`Cant kick member ${error}`);
    } finally {
      setLoadingId('');
    }
  };

  const onRoleChange = async (memberId: string, role: MemberRole) => {
    try {
      setLoadingId(memberId);
      const url = qs.stringifyUrl({
        url: `${API_BASE}/api/members/${memberId}`,
        query: {
          serverId: server?.id,
        },
      });

      const response = await fetch(url, {
        method: 'PATCH',
        body: JSON.stringify({ role }),
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();

      socket?.send(
        JSON.stringify({
          type: 'member-role-change',
          payload: data,
        })
      );
      // navigate(`/servers/${server.id}`, { replace: true });
    } finally {
      setLoadingId('');
      onClose();
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={handleClose} scrollBehavior={'inside'}>
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <div className="text-center text-2xl font-bold">
                {t('MembersModal.title')}
              </div>
              <div className="text-center">
                {server?.members?.length} {t('MembersModal.members')}
              </div>
            </ModalHeader>
            <ModalBody>
              <div className="mt-8">
                {server?.members?.map((member) => (
                  <div
                    key={member.id}
                    className="mb-6 flex items-center gap-x-2"
                  >
                    <UserAvatar src={member.profile.image || ''} />
                    <div className="flex flex-col gap-y-1">
                      <div className="flex items-center gap-x-1 text-xs font-semibold">
                        {member.profile.name}
                        {roleIconMap[member.role]}
                      </div>
                    </div>
                    {server.profileId !== member.profileId &&
                      loadingId !== member.id && (
                        <div className="relative ml-auto">
                          <button onClick={() => toggleMenu(member.id)}>
                            <MoreVertical className="size-4" />
                          </button>
                          {openMenuId === member.id && (
                            <div className="absolute right-0 top-full z-10 mt-2 w-48 rounded border bg-background text-sm shadow-md">
                              <div className="flex items-center px-4 py-2 font-semibold">
                                <ShieldQuestion className="mr-2 size-4" />
                                {t('MembersModal.role')}
                              </div>
                              <button
                                className="flex w-full items-center px-4 py-2"
                                onClick={() => {
                                  onRoleChange(member.id, MemberRole.GUEST);
                                  setOpenMenuId(null);
                                }}
                              >
                                <Shield className="mr-2 size-4" />
                                {t('MembersModal.guest')}
                                {member.role === 'GUEST' && (
                                  <Check className="ml-auto size-4" />
                                )}
                              </button>
                              <button
                                className="flex w-full items-center px-4 py-2"
                                onClick={() => {
                                  onRoleChange(member.id, MemberRole.MODERATOR);
                                  setOpenMenuId(null);
                                }}
                              >
                                <ShieldCheck className="mr-2 size-4" />
                                {t('MembersModal.moderator')}
                                {member.role === 'MODERATOR' && (
                                  <Check className="ml-auto size-4" />
                                )}
                              </button>
                              <button
                                className="flex w-full items-center px-4 py-2"
                                onClick={() => {
                                  onRoleChange(member.id, MemberRole.PRIVATE);
                                  setOpenMenuId(null);
                                }}
                              >
                                <LockIcon className="mr-2 size-4" />
                                {t('MembersModal.private')}
                                {member.role === 'PRIVATE' && (
                                  <Check className="ml-auto size-4" />
                                )}
                              </button>
                              <div className="my-1 border-t" />
                              <button
                                className="flex w-full items-center px-4 py-2 text-red-600"
                                onClick={() => {
                                  onKick(member.id);
                                  setOpenMenuId(null);
                                }}
                              >
                                <Gavel className="mr-2 size-4" />
                                {t('MembersModal.kick')}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    {loadingId === member.id && (
                      <Loader2 className="ml-auto size-4 animate-spin" />
                    )}
                  </div>
                ))}
              </div>
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
